import {
  ArrowPathIcon,
  DocumentPlusIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import React from 'react';
import { ActorRefFrom } from 'xstate';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { PARSEABLE_ACCEPT, PARSEABLE_TYPES_LABEL } from '../../../vault/documents/constants';
import { providerRegistrationView } from '../registration-view.machine';

interface Props {
  actorRef: ActorRefFrom<typeof providerRegistrationView>;
  docType: 'cv' | 'packet';
}

/**
 * Handles the display for a Drag/Drop upload of a CV or Credentialing Packet.
 */
export const CVUploadForm = ({ actorRef, docType }: Props) => {
  const { errorMsg, documentReference, parsing, parseOutcome, pendingFile } = useSelector(
    actorRef,
    (state) => state.context.cvUpload
  );

  const { dragging } = useSelector(actorRef, (state) => state.context.cvUpload);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    actorRef.send({ type: 'ON_DRAG_ENTER' });
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    actorRef.send({ type: 'ON_DRAG_LEAVE' });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Stages the file only; the doc type is asked for afterwards. [MCV-1593]
    actorRef.send({
      type: 'ON_UPLOAD_CV',
      files: e.dataTransfer.files,
    });
  };

  // While the parse runs the drop zone stays on the spinner (see UploadPrompt);
  // only a settled parse gets the summary card.
  if (documentReference && !parsing) {
    const successLabel = docType === 'packet' ? 'Credentialing Packet Uploaded' : 'CV Uploaded';
    const label = docType === 'packet' ? 'credentialing packet' : 'CV';
    return (
      <div className="flex flex-col w-full min-w-0 max-w-[465px]">
        <div
          data-cy={CT.registration.cvUpload.uploadCard}
          className="w-full min-h-[300px] mt-[30px] flex flex-col items-center justify-center rounded bg-primary-t-900 px-4 py-6 [overflow-wrap:anywhere]"
        >
          <div className="flex items-center justify-center w-16 h-16 shrink-0 rounded-pill bg-success shadow-[0_4px_12px_rgba(5,140,26,0.35)]">
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="mt-4 max-w-full text-lg font-semibold text-primary-500">{successLabel}</h3>
          <p className="mt-1 max-w-full text-sm text-grey-300">{documentReference.docName}</p>

          {/*
            How the AI parse ended. `complete` is the common path; the other two
            are honest about the fact that the vault may not be pre-filled, so
            nobody is left waiting on data that is not coming.
          */}
          {parseOutcome !== 'complete' && (
            <div className="mt-5 border-t border-primary-t-800 pt-4 w-full max-w-[340px]">
              <p className="text-xs text-grey-300 text-center">
                {parseOutcome === 'failed'
                  ? `We could not read this ${label} automatically. It is saved in your vault — you can fill in your details by hand.`
                  : `Still reading your ${label} with AI. This is taking longer than usual; it continues in the background and the details will appear in your vault.`}
              </p>
            </div>
          )}
          {parseOutcome === 'complete' && (
            <p className="mt-5 text-xs text-grey-300 text-center w-full max-w-[340px]">
              We read your {label} and pre-filled what we found.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full min-w-0 max-w-[465px]">
      {/*
        The classify question appears with no click of the user's to anchor it,
        so it has to be spoken. It cannot be spoken by ClassifyPrompt's own
        container: a live region is only announced for mutations made to a
        region that was ALREADY in the accessibility tree, and React mounts that
        container together with its content in a single commit -- region and
        text arrive as one insertion, which screen readers do not announce.

        So the region lives here instead, outside the branch that swaps, mounted
        for the whole life of the drop zone. Only its text child changes when a
        file is staged, which is the mutation shape that does get announced.
        [MCV-1593]
      */}
      <div role="status" aria-live="polite" aria-label="Document upload status" className="sr-only">
        {/*
          Worded differently from the visible question on purpose: identical
          copy in two places is read twice by anything that also picks up the
          prompt, and makes every text query in the suite ambiguous.
        */}
        {pendingFile
          ? `${pendingFile.name} is ready. Choose the document type: CV or Credentialing Packet.`
          : ''}
      </div>
      <div data-property-1="Default" className="w-full min-w-0 relative mt-[30px]">
        <div
          data-cy={CT.registration.cvUpload.uploadCard}
          className={classNames(
            'w-full min-h-[300px] rounded p-1 flex',
            'bg-[conic-gradient(from_96deg_at_100.00%_100.00%,var(--primary-t-800)_0deg,var(--primary-50)_360deg)]',
            'shadow-[7.200000762939453px_7.200000762939453px_11.160000801086426px_0px_rgba(209,209,209,0.30)]'
          )}
        >
          <div
            data-cy={CT.registration.cvUpload.dropzone}
            className={classNames(
              'w-full min-w-0 px-4 pb-6 [overflow-wrap:anywhere]',
              errorMsg ? 'bg-danger-subtle rounded' : 'bg-primary-t-900 rounded',
              dragging ? 'outline-4 bg-primary-t-800 -outline-offset-2 outline-primary-200' : ''
            )}
            onDragOver={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <UploadPrompt actorRef={actorRef} docType={docType} />
          </div>
        </div>
      </div>
    </div>
  );
};

interface PromptProps {
  actorRef: ActorRefFrom<typeof providerRegistrationView>;
  docType: 'cv' | 'packet';
}

/**
 *
 * Changes the view within the Upload Drag/Drop area based on state variables:
 * - dragging, uploading, uploaded, & errorMsg
 */
const UploadPrompt = ({ actorRef, docType }: PromptProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const onBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      actorRef.send({
        type: 'ON_UPLOAD_CV',
        files,
      });
    }
  };

  const { dragging, uploading, parsing, errorMsg, uploaded, name, type, size, pendingFile } =
    useSelector(actorRef, (state) => state.context.cvUpload);

  const browseButtonRef = React.useRef<HTMLButtonElement>(null);
  // "Choose a different file" unmounts the classify branch, and "Try uploading
  // again" unmounts the error branch; each takes the focused element with it --
  // focus falls to <body>, so a keyboard user has to tab back from the top of
  // the document to reach Browse Files again. Hand it to them instead.
  //
  // Gated on a ref set by the cancel and retry handlers rather than on
  // `!pendingFile` alone: this branch is also the first thing rendered on page load, and
  // grabbing focus there would yank it out of whatever the user was doing.
  const restoreFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (!pendingFile && !errorMsg && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      browseButtonRef.current?.focus();
    }
  }, [pendingFile, errorMsg]);

  if (errorMsg) {
    return (
      <ErrorMsg
        handleResetCVError={() => {
          restoreFocusRef.current = true;
          actorRef.send({ type: 'ON_RESET_CV_ERROR' });
        }}
        errorMsg={errorMsg}
      />
    );
  }
  // A file is staged but not classified yet, so ask what it is. [MCV-1593]
  //
  // BRANCH ORDER IS THE PRECEDENCE RULE HERE (MCV-1590). This sits after the
  // error check -- a failed upload's message must win -- and deliberately BEFORE
  // `uploading || parsing`, so the question is what the user sees between
  // selecting a file and answering. It is safe in front of the spinner only
  // because `pendingFile` is cleared in the same transition that sets
  // `uploading` (see ON_CLASSIFY_CV): gating on anything stickier, or moving
  // this below the spinner, re-creates the MCV-1590 shadowing bug.
  if (pendingFile) {
    return (
      <ClassifyPrompt
        fileName={pendingFile.name}
        onClassify={(chosen) => actorRef.send({ type: 'ON_CLASSIFY_CV', docType: chosen })}
        onCancel={() => {
          restoreFocusRef.current = true;
          actorRef.send({ type: 'ON_CANCEL_CLASSIFY' });
        }}
      />
    );
  }
  // In-flight work outranks the drag prompt: it is what the user is waiting on,
  // while `dragging` is a hover hint. Checking drag first let a latched flag
  // hide the spinner for the whole upload (MCV-1590).
  //
  // `parsing` shares this branch deliberately -- to the user, upload and AI read
  // are one wait, so the same spinner runs from drop until the parse settles.
  if (uploading || parsing) {
    const readLabel = docType === 'packet' ? 'credentialing packet' : 'CV';
    return (
      <>
        <Banner>
          <h1>{uploading ? 'Uploading...' : `Reading your ${readLabel}...`}</h1>
        </Banner>

        {uploading ? (
          <div className="mt-10 w-full min-w-0">
            <p>File: {name}</p>
            <p>type: {type}</p>
            <p>size: {size} (bytes) </p>
            <span className="mt-10">
              <LoadingSpinner bgColor={'bg-primary-t-900'} size={30} gap={0} speed={300} />{' '}
            </span>
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center px-2 sm:px-10">
            <p className="text-sm text-grey-300 text-center">
              {name} — our AI is pulling your details out now.
            </p>
            <span className="mt-6">
              <LoadingSpinner bgColor={'bg-primary-t-900'} size={30} gap={0} speed={300} />
            </span>
          </div>
        )}
      </>
    );
  }
  if (uploaded) {
    // Upload settled with no document reference, so the parse could not be
    // watched. The summary card above handles the normal path.
    return (
      <Banner>
        <h1 className="flex justify-center align-center">Upload Complete!</h1>
      </Banner>
    );
  }
  // Pre-drop copy stays NEUTRAL: the doc type is not chosen until after the file
  // is selected, so `docType` is still the default here and naming it would be a
  // guess. [MCV-1593]
  if (dragging) {
    return (
      <Banner>
        <h1>Drop & Load your document...</h1>
      </Banner>
    );
  }
  return (
    <div className="flex flex-col w-full items-center justify-center">
      <div className="flex flex-col w-full max-w-[318px] min-w-0 items-center justify-center">
        <div className="self-stretch mt-10 text-center justify-start text-primary-50 text-md font-semibold leading-5 ">
          Upload and process with AI
        </div>
        <div className="self-stretch mt-[14.4px] min-h-12 text-center justify-start text-grey-300 text-sm font-normal font-['Inter'] leading-5">
          Drag and Drop your CV or credentialing packet and we will pre-fill your vault with known
          fields
          <br />
          (Accepts {PARSEABLE_TYPES_LABEL})
        </div>

        <DocumentPlusIcon
          className="mt-[30px] h-10 w-10 stroke-1 stroke-primary-50 "
          aria-hidden="true"
        />

        <div className="mt-[30px] w-full min-h-5 text-center justify-start">
          <span className="text-grey-300 text-xs font-normal font-['Inter'] leading-5">
            Drag and Drop here or{' '}
          </span>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept={PARSEABLE_ACCEPT}
            onChange={handleFileInputChange}
          />
          <button
            ref={browseButtonRef}
            data-cy={CT.registration.cvUpload.browseFiles}
            onClick={onBrowseFiles}
            type="button"
            className="text-xs font-normal cursor-pointer underline"
          >
            Browse Files
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Asks what the just-selected file actually is. [MCV-1593]
 *
 * The answer has to precede the upload -- docCategory, workflow_type and
 * docDesc are all derived from it inside `api.uploadCV` and baked into the POST,
 * so unlike the vault flow we cannot upload in the background while asking.
 *
 * a11y: this carries the role="group" + aria-label the deleted pill toggle used
 * to provide. The ANNOUNCEMENT is deliberately not made from here -- see the
 * persistent live region in CVUploadForm for why a region mounted alongside its
 * own content cannot do it. What this branch owns instead is FOCUS: it replaces
 * the branch that held the Browse Files button, so without moving focus the
 * keyboard user is dumped back on <body> with the flow silently stopped, and
 * the announcement is the only hint anything happened. Moving focus onto the
 * labelled group is the reliable half of the pair.
 */
const ClassifyPrompt = ({
  fileName,
  onClassify,
  onCancel,
}: {
  fileName: string;
  onClassify: (docType: 'cv' | 'packet') => void;
  onCancel: () => void;
}) => {
  const groupRef = React.useRef<HTMLDivElement>(null);

  // Mount-only: re-focusing on every render would fight the user as they tab
  // between the two answers.
  React.useEffect(() => {
    groupRef.current?.focus();
  }, []);

  return (
    <div
      ref={groupRef}
      role="group"
      tabIndex={-1}
      aria-label="Document type"
      className="flex flex-col w-full items-center justify-center outline-none"
    >
      <div className="flex flex-col w-full max-w-[318px] min-w-0 items-center justify-center">
        <div className="self-stretch mt-10 text-center justify-start text-primary-50 text-md font-semibold leading-5">
          What kind of document is this?
        </div>

        {/* Let filenames wrap so the full name is readable on touch. */}
        <div
          title={fileName}
          className="self-stretch min-w-0 mt-[14.4px] text-center text-grey-300 text-sm font-normal font-['Inter'] leading-5 [overflow-wrap:anywhere]"
        >
          {fileName}
        </div>

        <div className="mt-[30px] flex flex-wrap justify-center gap-3">
          <button
            type="button"
            data-cy={CT.registration.cvUpload.docTypeCvButton}
            onClick={() => onClassify('cv')}
            className="px-4 py-2 rounded-full border text-sm font-medium bg-primary-50 text-white border-primary-50 cursor-pointer"
          >
            CV
          </button>
          <button
            type="button"
            data-cy={CT.registration.cvUpload.docTypePacketButton}
            onClick={() => onClassify('packet')}
            className="px-4 py-2 rounded-full border text-sm font-medium bg-primary-50 text-white border-primary-50 cursor-pointer"
          >
            Credentialing Packet
          </button>
        </div>

        {/*
          Without this the user is stuck: the Browse Files input lives in the
          default branch, which is unmounted while this one renders.
        */}
        <div className="mt-[24px]">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-normal text-grey-300 cursor-pointer underline"
          >
            Choose a different file
          </button>
        </div>
      </div>
    </div>
  );
};

const Banner = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-col w-full items-center justify-center">
      <div className="flex flex-col w-full max-w-[318px] min-w-0 items-center justify-center">
        <div className="self-stretch mt-10 text-center justify-start text-primary-500 text-md font-semibold leading-5 ">
          {children}
        </div>
      </div>
    </div>
  );
};

const ErrorMsg = ({
  handleResetCVError,
  errorMsg,
}: {
  handleResetCVError: () => void;
  errorMsg: string | undefined;
}) => {
  return (
    <div className="flex flex-col w-full items-center justify-center">
      <div className="flex flex-col w-full max-w-[318px] min-w-0 items-center justify-center">
        <div className="mt-[30px]">
          <ExclamationCircleIcon className="h-15 w-15 stroke-2 stroke-error" aria-hidden="true" />
        </div>
        <div
          className={
            'self-stretch mt-10 text-center justify-start text-error text-lg font-semibold leading-5 '
          }
        >
          {errorMsg}
        </div>

        <div className="mt-[30px]">
          <button
            type="button"
            onClick={handleResetCVError}
            aria-label="Try uploading again"
            className="cursor-pointer p-2 -m-2"
          >
            <ArrowPathIcon className="h-5 w-5 stroke-2 stroke-grey-100 " aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};
